import React from 'react';
import { FieldArray } from 'formik';
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AddIcon from '@mui/icons-material/Add';
import SalesPlanForm from './SalesPlanForm';

const SalesBillPlanFormList = ({ 
  formik, 
  ArrayHelperRef, 
  isManualVat, 
  setIsApplied, 
  planRowDelete, 
  targetExpSales, 
  tempTotalSalAmtSum 
}) => {
  
  // [기존 스타일 유지]
  const useStyles = makeStyles({
    table: {
      '& .MuiTableCell-root': {
        border: '1px solid rgba(224, 224, 224, 1)',
        align: 'center',
      },
    },
  });

  const classes = useStyles();

  // [로직 1] 부가세 재귀 반올림 함수
  const getRecursiveRound = (num) => {
    let value = num;
    for (let i = 4; i >= 0; i--) {
      const factor = Math.pow(10, i);
      value = Math.round(value * factor) / factor;
    }
    return Math.round(value);
  };

  // [로직 2] 날짜 성향(말일/일자 차이/주기) 분석 함수
  const getPatternedNextDate = (lastVal, prevVal) => {
    if (!lastVal) return new Date();
    const last = new Date(lastVal);
    if (!prevVal) return new Date(last.getTime());

    const prev = new Date(prevVal);
    const lastTotalMonths = last.getFullYear() * 12 + last.getMonth();
    const prevTotalMonths = prev.getFullYear() * 12 + prev.getMonth();
    const monthDiff = lastTotalMonths - prevTotalMonths;
    const dayDiff = last.getDate() - prev.getDate();
    
    // 마지막 행이 그 달의 말일이었는지 체크
    const isLastDayOfLastMonth = new Date(last.getFullYear(), last.getMonth() + 1, 0).getDate() === last.getDate();

    const next = new Date(last.getTime());

    if (monthDiff > 0) {
      next.setDate(1); // 날짜 튐 방지
      next.setMonth(next.getMonth() + monthDiff);

      if (isLastDayOfLastMonth) {
        // 말일 패턴 (5/31 -> 8/31 -> 11/30 등)
        const lastDayOfNextMonth = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
        next.setDate(lastDayOfNextMonth);
      } else {
        // 일반 고정 일자 또는 일수 변동 패턴
        const targetDay = last.getDate() + dayDiff;
        const lastDayOfNextMonth = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
        next.setDate(Math.min(Math.max(1, targetDay), lastDayOfNextMonth));
      }
    } else if (dayDiff !== 0) {
      next.setDate(last.getDate() + dayDiff);
    }
    return next;
  };

  // 데이터 참조 및 버튼 숨김 조건 계산
  const currentBills = formik.values?.purchaseBills || [];
  const lastRowData = currentBills.length > 0 ? currentBills[currentBills.length - 1] : null;

  // [핵심] 마지막 행의 매출 잔액(salBal)이 0이면 버튼 숨김 (소수점 오차 방지 포함)
  const isBalanceExhausted = lastRowData && Math.round(Number(String(lastRowData.salBal || 0).replace(/,/g, ""))) <= 0;

  return (
    <FieldArray
      name="purchaseBills"
      render={(arrayHelpers) => {
        ArrayHelperRef.current = arrayHelpers;
        return (
          <Box>
            <TableContainer
              size="small"
              className={classes.table}
              sx={{ width: '100%', display: 'table', tableLayout: 'fixed' }}
            >
              <Table padding="none">
                <TableHead>
                  {/* [기존 2단 헤더 구조 유지] */}
                  <TableRow>
                    <TableCell rowSpan={2} align="center">회차</TableCell>
                    <TableCell rowSpan={2} align="center">매출 일자<br/>(작성 일자)</TableCell>
                    <TableCell rowSpan={2} align="center">매출처</TableCell>
                    <TableCell rowSpan={2} align="center">매출 내용</TableCell>
                    <TableCell rowSpan={2} align="center">해외</TableCell>
                    <TableCell rowSpan={2} align="center">계약 금액</TableCell>
                    <TableCell rowSpan={2} align="center">매출 잔액</TableCell>
                    <TableCell colSpan={3} align="center">당 월 매출 금액</TableCell>
                    <TableCell rowSpan={2} align="center">수금 예정일</TableCell>
                    <TableCell rowSpan={2} align="center">발행 여부</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="center">매출 금액</TableCell>
                    <TableCell align="center">부가세</TableCell>
                    <TableCell align="center">매출 합계</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentBills.map((bill, index) => (
                    <SalesPlanForm
                      key={bill.waitUuid || index}
                      index={index}
                      bill={bill}
                      formik={formik}
                      isManualVat={isManualVat}
                      setIsApplied={setIsApplied}
                      planRowDelete={planRowDelete}
                      targetExpSales={targetExpSales}
                      tempTotalSalAmtSum={tempTotalSalAmtSum}
                    />
                  ))}

                  {/* [버튼] 마지막 행의 salBal이 0보다 클 때만 노출 */}
                  {currentBills.length > 0 && !isBalanceExhausted && (
                    <TableRow>
                      <TableCell colSpan={12} sx={{ p: 0 }}>
                        <Button
                          fullWidth
                          variant="text"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            const currentBills = formik.values.purchaseBills || [];
                            const lastRow = currentBills[currentBills.length - 1];
                            const secondLastRow = currentBills.length > 1 ? currentBills[currentBills.length - 2] : null;

                            // 1. 공급가액 잔액 확인 (마지막 행의 salBal 기준)
                            const remainingAmt = Math.round(Number(String(lastRow.salBal || 0).replace(/,/g, "")));
                            if (remainingAmt <= 0) return;

                            // 2. [핵심] 계약금액 기준 최종 목표 부가세 계산
                            const targetSales = Number(String(lastRow.expSales || 0).replace(/,/g, ""));
                            const finalTargetVat = getRecursiveRound(targetSales * 0.1); // 계약금액 * 0.1 적용

                            // 3. 현재까지 입력된 모든 행의 부가세 합계 계산
                            const currentTotalVat = currentBills.reduce((acc, cur) => {
                              return acc + Number(String(cur.salVat || 0).replace(/[^0-9.-]/g, ""));
                            }, 0);

                            // 4. 최종 목표 부가세에서 기입력 합계를 뺀 '남은 부가세' 결정
                            const remainingVat = finalTargetVat - currentTotalVat;

                            // 5. 날짜 및 시간 포맷팅
                            const nextSalDt = getPatternedNextDate(lastRow.salDt, secondLastRow?.salDt);
                            const nextColDt = getPatternedNextDate(lastRow.colDt, secondLastRow?.colDt);
                            
                            const now = new Date();
                            const formattedDate = now.getFullYear() + '-' + 
                                  String(now.getMonth() + 1).padStart(2, '0') + '-' + 
                                  String(now.getDate()).padStart(2, '0') + ' ' + 
                                  String(now.getHours()).padStart(2, '0') + ':' + 
                                  String(now.getMinutes()).padStart(2, '0') + ':' + 
                                  String(now.getSeconds()).padStart(2, '0');

                            // 6. 새 행 생성
                            const nextInvNo = (Number(lastRow.invNo) || 0) + 1;
                            const newRow = {
                              ...lastRow,
                              waitUuid: 'newRecord' + nextInvNo,
                              invNo: nextInvNo,
                              salDt: nextSalDt,
                              colDt: nextColDt,
                              salMonth: nextSalDt.getMonth() + 1,
                              salAmt: remainingAmt,       // 남은 공급가액 잔액
                              salVat: remainingVat,       // [적용] 계약금액*0.1에서 모자란 부가세 전액 투입
                              totalSalAmt: remainingAmt + remainingVat,
                              invYn: 'N',
                              salBal: 0,
                              crtUsr: lastRow.crtUsr,
                              crtDt: formattedDate 
                            };

                            formik.setFieldValue('purchaseBills', [...currentBills, newRow]);
                            setIsApplied(false); // 다시 '확인' 버튼을 누르도록 유도
                          }}

                          sx={{
                            py: 1.5,
                            color: 'primary.main',
                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' }
                          }}
                        >
                          계약금액 잔액 추가하기 (+)
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      }}
    />
  );
};

export default SalesBillPlanFormList;
